// ============================================================================
// WHATSAPP DEL ESTUDIO — conexión por QR, salud del número y pausa de envíos.
// (12-oct-2026, portado de la agenda dental: src/services/wahaService.ts + waPauseService.ts)
//
// Pega DIRECTO al núcleo lealify-base /api/messages/whatsapp* y /api/messages/wa-pause
// (same-origin, cookie de sesión). NO pasa por apiClient a propósito: allí todo lo que
// empieza por /messages va a /api/letsmove (mensajería interna de la vertical) y no se
// quiere arriesgar ese enrutado. Solo trae lo que usa la pantalla /whatsapp; envíos,
// campañas, etc. NO viven aquí.
//
// «¿Está aprovisionado?» (el isProxyConfigured de la agenda, que allá sale de
// /api/me/tenant → waha): aquí se resuelve con el propio GET /whatsapp-status, que
// devuelve `configured:false` cuando el tenant no tiene tenant_settings.whatsapp
// (lib/whatsapp.js sessionStatus). Una sola fuente, sin depender de /me/tenant.
// ============================================================================

export interface WhatsAppSessionStatus {
  connected: boolean;
  /** WORKING | SCAN_QR_CODE | STARTING | STOPPED | FAILED | ERROR | HTTP nnn | UNKNOWN */
  status?: string;
  deviceInfo?: { id?: string; pushName?: string };
  /** Aceptación del descargo (tenant_settings.whatsapp_disclaimer, compartido con agenda/manager). */
  disclaimer?: { accepted?: boolean; acceptedAt?: string; version?: string } | null;
  /** false = el estudio no tiene sesión aprovisionada: no hay QR posible. */
  configured: boolean;
  /** Hay una solicitud de activación viva (whatsapp_requests pending/provisioning). */
  requested?: boolean;
}

export interface WhatsAppQRResult {
  qrCode?: string | null;
  sessionStatus?: string;
  isConnected?: boolean;
  /** El backend respondió {configured:false}: nada que escanear, cortar el polling. */
  notConfigured?: boolean;
}

export interface WhatsAppActionResult { success: boolean; sessionStatus?: string; message?: string }

/** Salud del número (foto diaria de wa_health_snapshots; mismo shape que agenda/manager). */
export interface WaHealthComponent {
  max?: number; pts?: number | null; rate?: number | null;
  medidos?: number; respondieron?: number; via_reaccion?: number;
  proactivos_30d?: number; sin_respuesta_est?: number | null;
  dias?: number | null; envios_14d?: number; fallos_14d?: number;
  explica?: string;
  vivos?: number; contrapartes?: number; con_fuente?: boolean;
  n?: number; nuevos?: number; frios?: number; c30?: number; last?: string | null;
}

export interface WaGateStatus {
  activo: boolean;
  restriccion?: 'ninguna' | 'frio' | 'todo';
  motivo?: string | null;
  label?: string | null;
  hasta?: string | null;
  rechazos?: Array<{ motivo: string; label: string; n: number; numeros: number; ultima: string }>;
}

export interface WaHealth {
  available: boolean;
  score?: number;
  semaforo?: 'verde' | 'ambar' | 'rojo';
  veredicto?: string;
  session_status?: string;
  parts?: Record<string, WaHealthComponent> | null;
  measured_at?: string;
  stale?: boolean;
  gate?: WaGateStatus | null;
}

const BASE = '/api/messages';
const TIMEOUT_MS = 15000;

async function call<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...init,
      signal: ctrl.signal,
    });
    const text = await res.text();
    let body: any = null;
    if (text) { try { body = JSON.parse(text); } catch { body = text; } }
    if (!res.ok) throw new Error((body && body.error) || `HTTP ${res.status}`);
    return body as T;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw new Error('El servidor no respondió a tiempo');
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const post = <T = any>(path: string, body?: unknown) =>
  call<T>(path, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) });

// ── Estado / QR / salud (lecturas) ──────────────────────────────────────────

export async function checkSessionStatus(): Promise<WhatsAppSessionStatus> {
  const r = await call<any>('/whatsapp-status');
  return {
    connected: r.connected === true,
    status: r.status,
    deviceInfo: r.me ? { id: r.me.id, pushName: r.me.pushName } : undefined,
    disclaimer: r.disclaimer ?? null,
    configured: r.configured !== false,
    requested: r.requested === true,
  };
}

/** OJO: en el backend /whatsapp/qr se AUTO-RECUPERA (crea/reinicia la sesión). Solo se llama
 *  en modo «vinculando», nunca al cargar la página. */
export async function getQRCode(): Promise<WhatsAppQRResult> {
  const r = await call<any>('/whatsapp/qr');
  return { qrCode: r.qr ?? null, sessionStatus: r.status, isConnected: r.connected === true, notConfigured: r.configured === false };
}

/** Última foto de salud (lee BD, no toca la sesión). Ante fallo → no disponible. */
export async function getWhatsAppHealth(): Promise<WaHealth> {
  try { return await call<WaHealth>('/whatsapp/health'); } catch { return { available: false }; }
}

// ── Acciones (escrituras; solo las dispara la dueña desde la pantalla) ───────

const actionResult = (r: any): WhatsAppActionResult =>
  ({ success: r?.configured !== false && r?.status !== 'ERROR', sessionStatus: r?.status, message: r?.detail || undefined });

async function action(path: string): Promise<WhatsAppActionResult> {
  try { return actionResult(await post(path)); }
  catch (e) { return { success: false, message: e instanceof Error ? e.message : 'Error de conexión' }; }
}

export const startSession = () => action('/whatsapp/start');
export const restartSession = () => action('/whatsapp/restart');
export const logoutSession = () => action('/whatsapp/logout');

/** Versión vigente del texto del descargo (misma que agenda y manager). */
export const WHATSAPP_DISCLAIMER_VERSION = 'v1';

export async function acceptWhatsAppDisclaimer(): Promise<{ success: boolean; message?: string }> {
  try {
    const r = await post<any>('/whatsapp/disclaimer', { version: WHATSAPP_DISCLAIMER_VERSION });
    return r?.ok ? { success: true } : { success: false, message: 'No se pudo guardar la aceptación' };
  } catch (e) {
    return { success: false, message: e instanceof Error ? e.message : 'Error al guardar la aceptación' };
  }
}

/** Pide a plataforma activar el WhatsApp del estudio (crea whatsapp_requests y avisa a Lealify). */
export async function requestWhatsAppActivation(): Promise<{ success: boolean; message?: string }> {
  try {
    const r = await post<any>('/whatsapp/request', {});
    return r?.ok ? { success: true } : { success: false, message: 'No se pudo enviar la solicitud' };
  } catch (e) {
    return { success: false, message: e instanceof Error ? e.message : 'Error al enviar la solicitud' };
  }
}

// ── Pausa de mensajes automáticos (lib/waPause.js) ───────────────────────────
//   GET    /api/messages/wa-pause → { activa, level, until, by?, reason? }
//   PUT    /api/messages/wa-pause → body { level, minutes }
//   DELETE /api/messages/wa-pause → reanuda ya
// Nivel 1 = campañas y masivos · Nivel 2 = todo lo automático.

export interface WaPauseState { activa: boolean; level: 0 | 1 | 2; until: string | null; by?: string | null; reason?: string | null }

const PAUSE_OFF: WaPauseState = { activa: false, level: 0, until: null };

/** Ante cualquier fallo devuelve «sin pausa»: un error de red no debe pintar la pausa. */
export async function getWaPause(): Promise<WaPauseState> {
  try {
    const d = await call<any>('/wa-pause');
    return d?.activa ? (d as WaPauseState) : PAUSE_OFF;
  } catch {
    return PAUSE_OFF;
  }
}

export async function setWaPause(level: 1 | 2, minutes: number): Promise<WaPauseState> {
  return call<WaPauseState>('/wa-pause', { method: 'PUT', body: JSON.stringify({ level, minutes }) });
}

export async function clearWaPause(): Promise<WaPauseState> {
  await call('/wa-pause', { method: 'DELETE' });
  return PAUSE_OFF;
}

/** «hasta las 6:12 p.m.» — hora local del navegador, que es la del estudio. */
export function hastaTexto(until: string | null): string {
  if (!until) return '';
  const d = new Date(until);
  if (Number.isNaN(d.getTime())) return '';
  const hoy = new Date().toDateString() === d.toDateString();
  const hora = d.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true });
  return hoy ? `hasta las ${hora}` : `hasta mañana a las ${hora}`;
}
